/**
 * AppContext - Global state management for DexRiddle
 * Manages: theme, language, favorites, and pokemon cache
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AppContext = createContext(null);

// Translation strings
const translations = {
  es: {
    // Navigation
    home: 'Inicio',
    favorites: 'Favoritos',
    comparator: 'Comparador',
    search: 'Buscar Pokémon...',
    searchByName: 'Nombre o número',
    
    // Filters
    allTypes: 'Todos los tipos',
    allGenerations: 'Todas las generaciones',
    generation: 'Generación',
    sortBy: 'Ordenar por',
    number: 'Número',
    name: 'Nombre',
    totalStats: 'Stats totales',
    
    // Pokemon details
    height: 'Altura',
    weight: 'Peso',
    abilities: 'Habilidades',
    hiddenAbility: 'Oculta',
    stats: 'Estadísticas',
    moves: 'Movimientos',
    evolution: 'Evolución',
    weaknesses: 'Debilidades',
    resistances: 'Resistencias',
    immunities: 'Inmunidades',
    
    // Stats
    hp: 'PS',
    attack: 'Ataque',
    defense: 'Defensa',
    spAttack: 'At. Esp.',
    spDefense: 'Def. Esp.',
    speed: 'Velocidad',
    total: 'Total',
    
    // Move categories
    level: 'Nivel',
    tm: 'MT',
    tutor: 'Tutor',
    egg: 'Huevo',
    power: 'Potencia',
    accuracy: 'Precisión',
    category: 'Categoría',
    
    // Actions
    addFavorite: 'Añadir a favoritos',
    removeFavorite: 'Quitar de favoritos',
    compare: 'Comparar',
    previous: 'Anterior',
    next: 'Siguiente',
    showMore: 'Ver más',
    showLess: 'Ver menos',
    noResults: 'No se encontraron Pokémon',
    loading: 'Cargando...',
    error: 'Error al cargar',
    retry: 'Reintentar',
    
    // Misc
    shiny: 'Shiny',
    normal: 'Normal',
    regionalForms: 'Formas Regionales',
    noFavorites: 'No tienes favoritos aún',
    selectPokemon: 'Selecciona un Pokémon',
    vs: 'VS',
    description: 'Descripción',
    
    // Types
    types: {
      normal: 'Normal',
      fire: 'Fuego',
      water: 'Agua',
      electric: 'Eléctrico',
      grass: 'Planta',
      ice: 'Hielo',
      fighting: 'Lucha',
      poison: 'Veneno',
      ground: 'Tierra',
      flying: 'Volador',
      psychic: 'Psíquico',
      bug: 'Bicho',
      rock: 'Roca',
      ghost: 'Fantasma',
      dragon: 'Dragón',
      dark: 'Siniestro',
      steel: 'Acero',
      fairy: 'Hada'
    }
  },
  en: {
    // Navigation
    home: 'Home',
    favorites: 'Favorites',
    comparator: 'Comparator',
    search: 'Search Pokémon...',
    searchByName: 'Name or number',
    
    // Filters
    allTypes: 'All types',
    allGenerations: 'All generations',
    generation: 'Generation',
    sortBy: 'Sort by',
    number: 'Number',
    name: 'Name',
    totalStats: 'Total stats',
    
    // Pokemon details
    height: 'Height',
    weight: 'Weight',
    abilities: 'Abilities',
    hiddenAbility: 'Hidden',
    stats: 'Stats',
    moves: 'Moves',
    evolution: 'Evolution',
    weaknesses: 'Weaknesses',
    resistances: 'Resistances',
    immunities: 'Immunities',
    
    // Stats
    hp: 'HP',
    attack: 'Attack',
    defense: 'Defense',
    spAttack: 'Sp. Atk',
    spDefense: 'Sp. Def',
    speed: 'Speed',
    total: 'Total',
    
    // Move categories
    level: 'Level',
    tm: 'TM',
    tutor: 'Tutor',
    egg: 'Egg',
    power: 'Power',
    accuracy: 'Accuracy',
    category: 'Category',
    
    // Actions
    addFavorite: 'Add to favorites',
    removeFavorite: 'Remove from favorites',
    compare: 'Compare',
    previous: 'Previous',
    next: 'Next',
    showMore: 'Show more',
    showLess: 'Show less',
    noResults: 'No Pokémon found',
    loading: 'Loading...',
    error: 'Error loading',
    retry: 'Retry',
    
    // Misc
    shiny: 'Shiny',
    normal: 'Normal',
    regionalForms: 'Regional Forms',
    noFavorites: 'No favorites yet',
    selectPokemon: 'Select a Pokémon',
    vs: 'VS',
    description: 'Description',
    
    // Types
    types: {
      normal: 'Normal',
      fire: 'Fire',
      water: 'Water',
      electric: 'Electric',
      grass: 'Grass',
      ice: 'Ice',
      fighting: 'Fighting',
      poison: 'Poison',
      ground: 'Ground',
      flying: 'Flying',
      psychic: 'Psychic',
      bug: 'Bug',
      rock: 'Rock',
      ghost: 'Ghost',
      dragon: 'Dragon',
      dark: 'Dark',
      steel: 'Steel',
      fairy: 'Fairy'
    }
  }
};

export function AppProvider({ children }) {
  // Theme state (light by default)
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('dexriddle-theme') || 'light';
    }
    return 'light';
  });
  
  // Language state (English by default)
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('dexriddle-language') || 'en';
    }
    return 'en';
  });
  
  // Favorites state
  const [favorites, setFavorites] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('dexriddle-favorites');
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });
  
  // Pokemon cache (in-memory)
  const [pokemonCache, setPokemonCache] = useState({});
  // Mirror the cache in a ref so getFromCache can keep a stable identity and not
  // re-create every memoized fetcher (and re-run their effects) on each cache write.
  const pokemonCacheRef = useRef(pokemonCache);
  useEffect(() => { pokemonCacheRef.current = pokemonCache; }, [pokemonCache]);
  
  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('dexriddle-theme', theme);
  }, [theme]);
  
  // Save language to localStorage
  useEffect(() => {
    localStorage.setItem('dexriddle-language', language);
  }, [language]);
  
  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('dexriddle-favorites', JSON.stringify(favorites));
  }, [favorites]);
  
  // Toggle theme
  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, []);
  
  // Toggle language
  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'es' ? 'en' : 'es');
  }, []);
  
  // Add/remove favorite
  const toggleFavorite = useCallback((pokemonId) => {
    setFavorites(prev => {
      if (prev.includes(pokemonId)) {
        return prev.filter(id => id !== pokemonId);
      }
      return [...prev, pokemonId];
    });
  }, []);
  
  // Check if pokemon is favorite
  const isFavorite = useCallback((pokemonId) => {
    return favorites.includes(pokemonId);
  }, [favorites]);
  
  // Add to cache
  const addToCache = useCallback((key, data) => {
    setPokemonCache(prev => ({ ...prev, [key]: data }));
  }, []);
  
  // Get from cache (reads the ref so its identity stays stable across cache writes)
  const getFromCache = useCallback((key) => {
    return pokemonCacheRef.current[key];
  }, []);
  
  // Translation function
  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);
  
  const value = {
    theme,
    setTheme,
    toggleTheme,
    language,
    setLanguage,
    toggleLanguage,
    favorites,
    toggleFavorite,
    isFavorite,
    pokemonCache,
    addToCache,
    getFromCache,
    t
  };
  
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}

export default AppContext;
